import { useState } from "react";
import { DotLegalOverflowMenuItem } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { EntityType } from "@common/Common.types";
import { INoteDialogProps } from "@common/components/dotlegalNote/dotLegalNoteDialog/DotLegalNoteDialog";

interface NoteRow {
    id: string;
    note?: string | null;
}

export interface RowNoteDialogOptions<TRow extends NoteRow> {
    getPostUrl: (row: TRow) => string;
    onSaved: () => void;
    entityType?: EntityType;
    disableMentions?: boolean;
    getErrorText?: (row: TRow) => string;
}

// For tables that edit a row's note from the overflow menu: the menu item opens the dialog for that row, and the
// page renders the dialog once from noteDialogProps.
export function useRowNoteDialog<TRow extends NoteRow>(options: RowNoteDialogOptions<TRow>) {
    const { translateString } = useTranslation();
    const [notingRow, setNotingRow] = useState<TRow | null>(null);

    const getNoteMenuItem = (row: TRow): DotLegalOverflowMenuItem => ({
        menuItem: translateString(row.note ? "editNote" : "addNote"),
        onClick: () => setNotingRow(row),
    });

    const noteDialogProps: INoteDialogProps | undefined = notingRow
        ? {
              showDialog: true,
              noteText: notingRow.note ?? undefined,
              postUrl: options.getPostUrl(notingRow),
              errorText: options.getErrorText?.(notingRow),
              disableMentions: options.disableMentions,
              entityId: notingRow.id,
              entityType: options.entityType,
              onDialogClose: () => setNotingRow(null),
              onNoteUpdated: () => {
                  setNotingRow(null);
                  options.onSaved();
              },
          }
        : undefined;

    return { getNoteMenuItem, noteDialogProps };
}
